import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Redirect, Route, Router, Switch, useLocation } from 'wouter'
import { Navigation } from '../components/Navigation.js'
import { Footer } from '../components/Footer.js'
import { OnBoardingPage } from './onboarding/OnBoardingPage.js'
import { VotingPage } from './VotingPage.js'
import { CartPage } from './CartPage.js'
import { CheckoutPage } from './CheckoutPage/index.js'
import { ThankYouPage } from './ThankYouPage.js'
import { WinnerPage } from './WinnerPage.js'
import { AnnouncementModal } from '../components/AnnouncementModal.js'
import { useOnboarded } from '../hooks/useOnboarded.js'
import { useLive } from '../hooks/useLive.js'
import { useVotes } from '../hooks/useVotes.js'
import { useVotingRegion } from '../hooks/useVotingRegion.js'
import { useLanguage } from '../hooks/useLanguage.js'
import { setLocalStorage } from '@based/use-local-storage'
import { TABLET, MOBILE } from '../utils/breakpoints.js'
import { Background } from '../components/Background.js'
import { AppCtx, useAppCtx, useContentQuery } from '../ctx.js'
import { Privacy } from './Legal/Privacy.js'
import { Terms } from './Legal/Terms.js'
import { InfradownPage } from './InfradownPage.js'
import { ContentPage } from './ContentPage.js'
import { useScrollToTop } from '../hooks/useScrollToTop.js'
import { Imprint } from './Legal/Imprint.js'
import { VotingIntegrity } from './Legal/VotingIntegrity.js'

const AppCtxProvider = ({ children }: { children: ReactNode }) => {
  const parent = useAppCtx()
  const [{ currency, price, maxVotes }] = useVotingRegion()
  const [{ code: activeLanguage }] = useLanguage()
  const [votes] = useVotes()
  const votesCount = Object.values(votes ?? {}).reduce((a, b) => a + b, 0)

  return (
    <AppCtx.Provider
      value={{
        ...parent,
        activeLanguage,
        votesCount,
        minVotes: 1,
        maxVotes,
        price,
        currency,
      }}
    >
      {children}
    </AppCtx.Provider>
  )
}

export const App = ({ base }: { base?: string } = {}) => (
  <Router base={base}>
    <AppCtxProvider>
      <AppInner />
    </AppCtxProvider>
  </Router>
)

const AppInner = () => {
  const [onboarded] = useOnboarded()
  const [location] = useLocation()

  const [openAnnouncementModal, setOpenAnnouncementModal] = useState(false)

  const { loading, votingRegion, name, round, rounds, ...data } = useLive()

  let params = new URLSearchParams(document.location.search)
  let isProcessingPayment = params.get('loading')

  const {
    data: announcement,
    checksum,
    loading: loadingAnnouncement,
  } = useContentQuery('announcement')

  const [votes, setVotes] = useVotes()

  const prevVotesKey = useRef<string | undefined>(undefined)

  useScrollToTop()

  useEffect(() => {
    if (!name || !votingRegion?.code) return
    const newKey = `2026votes-${name}-${votingRegion.code}`
    if (prevVotesKey.current && prevVotesKey.current !== newKey) {
      setLocalStorage(prevVotesKey.current, null)
      setVotes({})
    }
    prevVotesKey.current = newKey
  }, [name, votingRegion?.code])

  useEffect(() => {
    if (loadingAnnouncement) {
      return
    }
    if (!announcement) {
      setOpenAnnouncementModal(false)
    }
    if (announcement) {
      setOpenAnnouncementModal(true)
    }
  }, [checksum])

  const [loadingGracePassed, setLoadingGracePassed] = useState(false)
  useEffect(() => {
    if (!loading) {
      setLoadingGracePassed(false)
      return
    }
    const timeout = setTimeout(() => setLoadingGracePassed(true), 3000)
    return () => clearTimeout(timeout)
  }, [loading])

  if (loading) {
    return loadingGracePassed ? <InfradownPage /> : null
  }

  const isWinner = name === 'winner'

  const showFooter =
    onboarded &&
    !location.startsWith('/thank-you') &&
    !location.startsWith('/checkout')

  const showNav = showFooter

  const hideCart =
    !data.isVoting ||
    location.startsWith('/cart') ||
    votingRegion?.voteType !== 'online'

  const animationType = !onboarded
    ? 'onboarding'
    : location.startsWith('/checkout') ||
        location.startsWith('/cart') ||
        location.startsWith('/thank-you')
      ? 'payment'
      : 'voting'

  let homePage: ReactNode

  if (!onboarded) {
    homePage = <OnBoardingPage />
  } else if (data.isVoting) {
    const votesKey = name + '-' + (votingRegion?.code ?? '')

    homePage = (
      <VotingPage
        key={votesKey}
        votes={votes}
        setVotes={setVotes}
        round={round}
        rounds={rounds}
        name={name}
        {...data}
      />
    )
  } else if (name === 'winner') {
    homePage = <WinnerPage {...data} />
  } else {
    homePage = <ContentPage rounds={rounds} {...data} />
  }

  return (
    <>
      {showNav && !isWinner && <Navigation votes={votes} hideCart={hideCart} />}
      <Switch>
        <Route path="/">{homePage}</Route>

        <Route path="/cart">
          <CartPage votes={votes} setVotes={setVotes} />
        </Route>

        <Route path="/checkout">
          <CheckoutPage votes={votes} />
        </Route>

        <Route path="/terms-of-service">
          <Terms />
        </Route>
        <Route path="/privacy-policy">
          <Privacy />
        </Route>
        <Route path="/imprint">
          <Imprint />
        </Route>
        <Route path="/voting-integrity">
          <VotingIntegrity />
        </Route>

        <Route path="/thank-you/*">
          <ThankYouPage />
        </Route>

        <Route>
          <Redirect to="/" />
        </Route>
      </Switch>
      {openAnnouncementModal && (
        <AnnouncementModal
          setModal={setOpenAnnouncementModal}
          announcement={announcement}
        />
      )}
      {showFooter && (
        <Footer
          votes={votes}
          hideCart={hideCart}
          style={{
            [TABLET]: {
              paddingTop: 48,
              paddingRight: 48,
              paddingLeft: 48,
              paddingBottom: data.isVoting && location === '/' ? 112 : 48,
            },
            [MOBILE]: {
              paddingTop: 16,
              paddingRight: 16,
              paddingLeft: 16,
              paddingBottom: data.isVoting && location === '/' ? 112 : 24,
            },
          }}
        />
      )}

      {!isProcessingPayment && <Background motion={1} type={animationType} />}
    </>
  )
}
